import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BellRing, Gauge, Mail, Smartphone } from 'lucide-react';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { UI_FEEDBACK_MESSAGES } from '@/constants/uiLabels';

const defaultPreferences = {
  notification_preferences: {
    email_enabled: true,
    push_enabled: true,
  },
  email_preferences: {
    transactional: true,
    marketing: true,
    reminders: true,
  },
  push_preferences: {
    transactional: true,
    marketing: true,
    reminders: true,
  },
};

const scoreBandStyles = {
  active: 'bg-emerald-100 text-emerald-700',
  medium: 'bg-amber-100 text-amber-700',
  inactive: 'bg-slate-100 text-slate-700',
};

const scoreBandLabels = {
  active: 'Actif',
  medium: 'Modere',
  inactive: 'Inactif',
};

const NotificationPreferencesSection = () => {
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const preferencesResponse = await axios.get(`${API}/user/notification-preferences`);
      setPreferences({
        ...defaultPreferences,
        ...(preferencesResponse.data?.data?.preferences || {}),
      });
      setScore(preferencesResponse.data?.data?.score || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger vos preferences notifications.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const updateNestedPreference = (section, key, value) => {
    setPreferences((current) => ({
      ...current,
      [section]: {
        ...(current[section] || {}),
        [key]: value,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.patch(`${API}/user/notification-preferences`, {
        notification_preferences: preferences.notification_preferences,
        email_preferences: preferences.email_preferences,
        push_preferences: preferences.push_preferences,
      });
      setPreferences({
        ...defaultPreferences,
        ...(response.data?.data?.preferences || {}),
      });
      setScore(response.data?.data?.score || null);
      toast.success(UI_FEEDBACK_MESSAGES.notificationPreferencesSaved);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible d enregistrer vos preferences notifications.');
    } finally {
      setSaving(false);
    }
  };

  const scoreSummary = useMemo(() => {
    const band = String(score?.activity_band || 'inactive').toLowerCase();
    if (band === 'active') {
      return 'Votre activite recente est solide. Nous limitons naturellement les relances les moins utiles.';
    }
    if (band === 'medium') {
      return 'Votre compte reste actif. Les rappels utiles continuent pour garder un bon rythme.';
    }
    return 'Votre activite recente est faible. Nous privilegions quelques relances utiles sans sur-sollicitation.';
  }, [score]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
        Chargement de vos preferences notifications...
      </div>
    );
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BellRing className="h-5 w-5 text-red-600" />
              Canaux globaux
            </CardTitle>
            <CardDescription>
              Coupez un canal complet si vous ne souhaitez plus recevoir de communications non critiques dessus.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <PreferenceRow
              label="Emails"
              description="Active ou coupe les emails non critiques."
              checked={!!preferences.notification_preferences?.email_enabled}
              onCheckedChange={(value) => updateNestedPreference('notification_preferences', 'email_enabled', value)}
            />
            <PreferenceRow
              label="Push mobile"
              description="Active ou coupe les notifications push non critiques."
              checked={!!preferences.notification_preferences?.push_enabled}
              onCheckedChange={(value) => updateNestedPreference('notification_preferences', 'push_enabled', value)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-red-600" />
              Preferences email
            </CardTitle>
            <CardDescription>
              Gerez separement les emails transactionnels, marketing et les rappels utiles.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <PreferenceRow
              label="Emails transactionnels"
              description="Paiements, KYC, confirmations metier et autres informations operationnelles."
              checked={!!preferences.email_preferences?.transactional}
              onCheckedChange={(value) => updateNestedPreference('email_preferences', 'transactional', value)}
            />
            <PreferenceRow
              label="Emails marketing"
              description="Newsletter, nouveautes produit et communications commerciales."
              checked={!!preferences.email_preferences?.marketing}
              onCheckedChange={(value) => updateNestedPreference('email_preferences', 'marketing', value)}
            />
            <PreferenceRow
              label="Rappels utiles"
              description="Relances legeres, onboarding et rappels d action quand ils restent pertinents."
              checked={!!preferences.email_preferences?.reminders}
              onCheckedChange={(value) => updateNestedPreference('email_preferences', 'reminders', value)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-red-600" />
              Preferences push
            </CardTitle>
            <CardDescription>
              Les push servent surtout aux messages mobiles utiles. Vous pouvez les ajuster finement ici.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <PreferenceRow
              label="Push transactionnels"
              description="KYC, paiement confirme, recharge wallet confirmee, mission assignee."
              checked={!!preferences.push_preferences?.transactional}
              onCheckedChange={(value) => updateNestedPreference('push_preferences', 'transactional', value)}
            />
            <PreferenceRow
              label="Push marketing"
              description="Communications promotionnelles ou marketing mobile futures."
              checked={!!preferences.push_preferences?.marketing}
              onCheckedChange={(value) => updateNestedPreference('push_preferences', 'marketing', value)}
            />
            <PreferenceRow
              label="Push de rappel"
              description="Relances mobiles ciblees quand une action merite un rappel."
              checked={!!preferences.push_preferences?.reminders}
              onCheckedChange={(value) => updateNestedPreference('push_preferences', 'reminders', value)}
            />
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button className="bg-red-500 hover:bg-red-600" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : 'Enregistrer mes preferences'}
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        <Card className="border-red-100 bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gauge className="h-5 w-5 text-red-600" />
              Score d activite
            </CardTitle>
            <CardDescription>
              Ce score simple aide Coli Noki a eviter des relances inutiles et a mieux cibler les rappels.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">{Number(score?.score || 0)}/100</p>
                <p className="text-sm text-gray-600">{scoreSummary}</p>
              </div>
              <Badge className={scoreBandStyles[String(score?.activity_band || 'inactive').toLowerCase()] || scoreBandStyles.inactive}>
                {scoreBandLabels[String(score?.activity_band || 'inactive').toLowerCase()] || 'Inactif'}
              </Badge>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
              <p className="font-medium text-gray-900">Ce que cela change</p>
              <ul className="mt-2 space-y-2">
                <li>Les utilisateurs tres actifs recoivent moins de rappels repetitifs.</li>
                <li>Les utilisateurs inactifs recoivent des relances plus ciblees.</li>
                <li>Les campagnes marketing futures pourront etre mieux personnalisees.</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Bon a savoir</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-700">
            <p>Les emails de securite et de reinitialisation restent toujours actifs pour proteger votre compte.</p>
            <p>Si aucun token push actif n est enregistre sur votre appareil, les messages push sont ignores proprement.</p>
            <p>Vos preferences sont stockees cote backend et utilisees par les automations email et push.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const PreferenceRow = ({ label, description, checked, onCheckedChange }) => (
  <div className="flex items-center justify-between gap-4">
    <div>
      <Label className="text-base font-medium">{label}</Label>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onCheckedChange} />
  </div>
);

export default NotificationPreferencesSection;
